<template>
  <SplitPanelShell
    class="draft-bills-history"
    title="History"
    icon="bi bi-clock-history"
    :count="records.length || null"
    layout="single"
    :status-message="statusMessage"
    :status-type="statusType"
  >
    <template #actions>
      <SecondaryButton
        icon="bi bi-arrow-clockwise"
        label=""
        custom-class="split-panel-shell__icon-action"
        :loading="loading"
        :disabled="loading"
        title="Refresh history"
        @click="loadHistory"
      />
    </template>

    <template #content>
      <div v-if="loading && !records.length" class="split-panel-shell__empty">
        <i class="bi bi-arrow-repeat spinning" aria-hidden="true"></i>
        <p>Loading audit history...</p>
      </div>

      <div v-else-if="!records.length" class="split-panel-shell__empty">
        <div class="split-panel-shell__empty-icon">
          <i class="bi bi-clock-history" aria-hidden="true"></i>
        </div>
        <h4>No posted captures yet</h4>
        <p>When a captured invoice or vendor-portal submission is verified and posted, the audit record appears here.</p>
      </div>

      <div v-else class="split-panel-shell__list-scroll draft-bills-history__scroll">
        <article
          v-for="row in records"
          :key="row.id"
          class="audit-voucher"
        >
          <header class="audit-voucher__head">
            <div class="audit-voucher__title">
              <span class="audit-voucher__kicker">{{ isVendorPortalHistory(row) ? 'Vendor portal' : 'Vendor bill' }}</span>
              <h3>{{ vendorName(row) }}</h3>
              <span class="audit-voucher__status">{{ isVendorPortalHistory(row) ? 'Verified' : 'Posted' }}</span>
            </div>
            <dl class="audit-voucher__facts">
              <div>
                <dt>Document no.</dt>
                <dd>{{ postedBillNo(row) }}</dd>
              </div>
              <div>
                <dt>Amount</dt>
                <dd>{{ formatMoney(posted(row).total || draft(row).total) }}</dd>
              </div>
              <div>
                <dt>Bill date</dt>
                <dd>{{ formatDate(posted(row).date) }}</dd>
              </div>
              <div>
                <dt>Closed</dt>
                <dd>{{ formatWhen(row.completedAt) }}</dd>
              </div>
            </dl>
          </header>

          <ol class="audit-voucher__flow" aria-label="Posting sequence">
            <li>
              <span>1</span>
              {{ isVendorPortalHistory(row) ? 'Vendor submitted' : 'Draft bill' }}
            </li>
            <li>
              <span>2</span>
              {{ isVendorPortalHistory(row) ? 'Company review' : 'Captured mail' }}
            </li>
            <li>
              <span>3</span>
              {{ isVendorPortalHistory(row) ? 'Verified' : 'Review &amp; adjust' }}
            </li>
            <li class="is-done">
              <span>4</span>
              Posted as open bill
            </li>
          </ol>

          <div class="audit-voucher__docs">
            <section class="audit-doc audit-doc--draft">
              <header>
                <i class="bi bi-file-earmark-text" aria-hidden="true"></i>
                <div>
                  <h4>Source draft</h4>
                  <p>Original bill before review</p>
                </div>
                <em>Draft</em>
              </header>
              <dl>
                <div><dt>Document no.</dt><dd>{{ draft(row).billNo || '—' }}</dd></div>
                <div><dt>Document date</dt><dd>{{ formatDate(draft(row).date) }}</dd></div>
                <div><dt>Due date</dt><dd>{{ formatDate(draft(row).dueDate) }}</dd></div>
                <div><dt>Terms</dt><dd>{{ termsLabel(draft(row)) }}</dd></div>
                <div class="is-amount"><dt>Amount</dt><dd>{{ formatMoney(draft(row).total) }}</dd></div>
              </dl>
            </section>

            <section class="audit-doc audit-doc--mail">
              <header>
                <i :class="isVendorPortalHistory(row) ? 'bi bi-cloud-upload' : 'bi bi-envelope-paper'" aria-hidden="true"></i>
                <div>
                  <h4>{{ isVendorPortalHistory(row) ? 'Vendor submission' : 'Supporting mail' }}</h4>
                  <p>{{ isVendorPortalHistory(row) ? 'Invoice submitted from the vendor portal' : 'Vendor evidence used for the posting' }}</p>
                </div>
                <em>{{ isVendorPortalHistory(row) ? 'Portal' : 'Captured' }}</em>
              </header>
              <dl>
                <div><dt>From</dt><dd>{{ mailFrom(row) }}</dd></div>
                <div><dt>Subject</dt><dd>{{ mailSubject(row) }}</dd></div>
                <div><dt>Received</dt><dd>{{ formatWhen(mail(row).receivedDateTime) }}</dd></div>
              </dl>
              <div v-if="billFiles(row).length" class="audit-files">
                <span>Bill attachment</span>
                <button
                  v-for="file in billFiles(row)"
                  :key="`${row.id}-${file.key}`"
                  type="button"
                  class="audit-files__btn"
                  :disabled="previewLoadingKey === file.key"
                  @click="previewAttachment(row, file)"
                >
                  <i :class="fileIcon(file.name)" aria-hidden="true"></i>
                  <span>{{ file.name }}</span>
                  <i
                    v-if="previewLoadingKey === file.key"
                    class="bi bi-arrow-repeat spinning"
                    aria-hidden="true"
                  ></i>
                  <i v-else class="bi bi-eye" aria-hidden="true"></i>
                </button>
              </div>
            </section>

            <section class="audit-doc audit-doc--posted">
              <header>
                <i class="bi bi-journal-check" aria-hidden="true"></i>
                <div>
                  <h4>Posted bill</h4>
                  <p>Open accounts payable after review</p>
                </div>
                <em>Open</em>
              </header>
              <dl>
                <div><dt>Document no.</dt><dd>{{ posted(row).billNo || '—' }}</dd></div>
                <div><dt>Document date</dt><dd>{{ formatDate(posted(row).date) }}</dd></div>
                <div><dt>Due date</dt><dd>{{ formatDate(posted(row).dueDate) }}</dd></div>
                <div><dt>Terms</dt><dd>{{ termsLabel(posted(row)) }}</dd></div>
                <div class="is-amount"><dt>Amount</dt><dd>{{ formatMoney(posted(row).total) }}</dd></div>
              </dl>
            </section>
          </div>

          <section class="audit-adjust">
            <header>
              <div>
                <h4>Adjustments</h4>
                <p>Changes made before the draft was posted. Unchanged fields are omitted.</p>
              </div>
              <span>{{ adjustmentCount(row) }}</span>
            </header>

            <p v-if="!visibleAdjustments(row).length" class="audit-adjust__none">
              No amount, date, or reference changes. The draft was posted as captured.
            </p>

            <table v-else>
              <thead>
                <tr>
                  <th scope="col">Field</th>
                  <th scope="col">Original draft</th>
                  <th scope="col">Posted value</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(change, index) in visibleAdjustments(row)" :key="`${row.id}-${index}`">
                  <th scope="row">{{ change.field }}</th>
                  <td>{{ displayValue(change.before) }}</td>
                  <td>{{ displayValue(change.after) }}</td>
                </tr>
              </tbody>
            </table>
          </section>
        </article>
      </div>

      <Teleport to="body">
      <div
        v-if="preview"
        class="audit-viewer"
        role="dialog"
        aria-modal="true"
        :aria-label="preview.name || 'Attachment preview'"
      >
        <div class="audit-viewer__backdrop" @click="closePreview"></div>
        <section class="audit-viewer__sheet">
          <header class="audit-viewer__bar">
            <div>
              <span>Bill attachment</span>
              <strong>{{ preview.name }}</strong>
            </div>
            <div class="audit-viewer__actions">
              <button type="button" @click="openPreviewTab">Open in new tab</button>
              <button type="button" class="audit-viewer__close" @click="closePreview">Close</button>
            </div>
          </header>
          <p v-if="preview.error" class="audit-viewer__message">{{ preview.error }}</p>
          <div v-else-if="preview.kind === 'image'" class="audit-viewer__page">
            <img :src="preview.url" :alt="preview.name" />
          </div>
          <iframe
            v-else-if="preview.kind === 'pdf'"
            class="audit-viewer__page"
            :src="preview.url"
            :title="preview.name"
          ></iframe>
          <p v-else class="audit-viewer__message">
            This file cannot be shown here.
            <button type="button" @click="openPreviewTab">Open file</button>
          </p>
        </section>
      </div>
      </Teleport>
    </template>
  </SplitPanelShell>
</template>

<script>
import logger from '@/utils/logger';
import ApiService from '@/services/api.service';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import SplitPanelShell from '@/modules/vendor/components/SplitPanelShell.vue';

export default {
  name: 'DraftBillsHistoryPanel',
  components: {
    SecondaryButton,
    SplitPanelShell,
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      records: [],
      loading: false,
      statusMessage: '',
      statusType: '',
      preview: null,
      previewLoadingKey: '',
      billAttachmentCache: {},
    };
  },
  computed: {
    companyId() {
      return this.$route?.params?.id || this.$route?.params?.companyId || null;
    },
  },
  watch: {
    active(isActive) {
      if (isActive) this.loadHistory();
    },
    companyId() {
      if (this.active) this.loadHistory();
    },
  },
  mounted() {
    if (this.active) this.loadHistory();
  },
  beforeUnmount() {
    this.closePreview();
  },
  methods: {
    draft(row) {
      return row?.draftSnapshot || {};
    },
    isVendorPortalHistory(row) {
      const service = String(row?.mailService || this.mail(row).mailService || '').toLowerCase();
      return service === 'vendor-portal' || String(row?.messageId || '').startsWith('vendor-portal-');
    },
    mail(row) {
      return row?.capturedMail || {};
    },
    posted(row) {
      return row?.postedSnapshot || {};
    },
    postedBillNo(row) {
      return this.posted(row).billNo || this.draft(row).billNo || `Bill ${row.billId || ''}`;
    },
    vendorName(row) {
      return this.posted(row).vendorName || this.draft(row).vendorName || this.mail(row).fromName || 'Vendor';
    },
    mailFrom(row) {
      const mail = this.mail(row);
      if (mail.fromName && mail.fromAddress) return `${mail.fromName} · ${mail.fromAddress}`;
      return mail.fromName || mail.fromAddress || '—';
    },
    mailSubject(row) {
      const mail = this.mail(row);
      const subject = String(mail.subject || '').trim();
      if (subject) return subject;
      const preview = String(mail.bodyPreview || mail.summary || '').trim();
      if (preview) return preview.length > 90 ? `${preview.slice(0, 90)}…` : preview;
      return 'Vendor invoice mail';
    },
    postedAttachmentNames(row) {
      return (this.posted(row).attachments || [])
        .map((name) => String(name || '').trim().toLowerCase())
        .filter(Boolean);
    },
    billFiles(row) {
      const billId = row?.billId || row?.draftBillId;
      const saved = billId ? this.billAttachmentCache[billId] || [] : [];
      const postedNames = this.postedAttachmentNames(row);
      const billNo = String(this.posted(row).billNo || '').toLowerCase();
      const related = saved.filter((file) => {
        const name = String(file.fileName || file.name || '').trim().toLowerCase();
        if (!name) return false;
        if (postedNames.length && !postedNames.includes(name)) return false;
        return true;
      });
      const invoice = related.filter((file) => {
        const name = String(file.fileName || file.name || '').trim().toLowerCase();
        const stem = name.replace(/\.[a-z0-9]+$/, '');
        return billNo.includes(stem) || stem.includes('invoice');
      });
      const chosen = invoice.length ? invoice : related.slice(0, 1);
      return chosen.map((file) => ({
        key: String(file.fileName || file.name || file.id).toLowerCase(),
        id: file.id || null,
        name: file.fileName || file.name,
        contentType: file.mimeType || file.contentType || '',
        filePath: file.filePath || null,
      }));
    },
    fileIcon(name) {
      const ext = String(name || '').split('.').pop().toLowerCase();
      if (ext === 'pdf') return 'bi bi-file-earmark-pdf-fill';
      if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'bi bi-file-earmark-image-fill';
      return 'bi bi-file-earmark-fill';
    },
    previewKind(name, mime) {
      const type = String(mime || '').toLowerCase();
      const ext = String(name || '').split('.').pop().toLowerCase();
      if (type.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'image';
      if (type.includes('pdf') || ext === 'pdf') return 'pdf';
      return 'other';
    },
    closePreview() {
      if (this.preview?.url) URL.revokeObjectURL(this.preview.url);
      this.preview = null;
      this.previewLoadingKey = '';
    },
    openPreviewTab() {
      if (!this.preview?.url) return;
      window.open(this.preview.url, '_blank', 'noopener');
    },
    async postedBillAttachments(row) {
      const billId = row?.billId || row?.draftBillId;
      if (!billId) return [];
      if (this.billAttachmentCache[billId]) return this.billAttachmentCache[billId];
      const bill = await ApiService.getEnterBillById(billId);
      const files = Array.isArray(bill?.attachments) ? bill.attachments : [];
      this.billAttachmentCache = { ...this.billAttachmentCache, [billId]: files };
      return files;
    },
    async fetchAttachmentBlob(row, file) {
      const saved = await this.postedBillAttachments(row);
      const match = saved.find((item) =>
        String(item.fileName || item.name || '').trim().toLowerCase() === file.name.toLowerCase()
        && (!file.filePath || item.filePath === file.filePath || item.id === file.id)
      ) || saved.find((item) =>
        String(item.fileName || item.name || '').trim().toLowerCase() === file.name.toLowerCase()
      );
      if (!match?.filePath) {
        throw new Error('This file is not saved on this bill');
      }
      if (match?.filePath) {
        const filename = String(match.filePath).includes('/')
          ? String(match.filePath).split('/').pop()
          : match.filePath;
        const response = await ApiService.get(
          `/vendor/documents/${encodeURIComponent(filename)}?companyId=${encodeURIComponent(this.companyId)}`,
          { responseType: 'blob' }
        );
        const mime = response.headers?.['content-type'] || match.mimeType || file.contentType || 'application/pdf';
        return new Blob([response.data], { type: mime });
      }
      throw new Error('This file is not saved on this bill');
    },
    async previewAttachment(row, file) {
      if (!file?.name) return;
      this.previewLoadingKey = file.key;
      this.closePreview();
      this.previewLoadingKey = file.key;
      try {
        const blob = await this.fetchAttachmentBlob(row, file);
        const url = URL.createObjectURL(blob);
        this.preview = {
          recordId: row.id,
          name: file.name,
          url,
          kind: this.previewKind(file.name, blob.type || file.contentType),
          error: '',
        };
      } catch (error) {
        logger.error('Could not preview history attachment:', error);
        this.preview = {
          recordId: row.id,
          name: file.name,
          url: '',
          kind: 'other',
          error: error.message || 'Could not preview this attachment',
        };
      } finally {
        this.previewLoadingKey = '';
      }
    },
    termsLabel(snapshot) {
      const label = String(snapshot?.paymentTermLabel || '').trim();
      if (label) return label;
      const term = snapshot?.paymentTerm;
      if (term == null || term === '') return '—';
      // Avoid showing raw numeric ids as "Terms"
      if (Number.isFinite(Number(term)) && String(term).trim() === String(Number(term))) return '—';
      return String(term);
    },
    adjustmentCount(row) {
      const count = this.visibleAdjustments(row).length;
      return count === 1 ? '1 change' : `${count} changes`;
    },
    visibleAdjustments(row) {
      return (row?.adjustments || []).filter((change) => {
        const field = String(change?.field || '');
        return field && field !== 'Items' && field !== 'Consultant hours';
      }).map((change) => ({
        ...change,
        before: this.readableChange(change.field, change.before),
        after: this.readableChange(change.field, change.after),
      }));
    },
    readableChange(field, value) {
      if (field === 'Attachments') {
        return String(value || '')
          .split(',')
          .map((name) => name.trim())
          .filter(Boolean)
          .join(', ');
      }
      if (field === 'Amount') return this.formatMoney(value);
      if (field === 'Bill date' || field === 'Due date') return this.formatDate(value);
      return value;
    },
    formatMoney(value) {
      const amount = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
      if (!Number.isFinite(amount)) return '—';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }).format(amount);
    },
    formatDate(value) {
      if (!value) return '—';
      let date = value;
      if (!(value instanceof Date)) {
        const text = String(value).trim();
        const us = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
        date = us
          ? new Date(Number(us[3]), Number(us[1]) - 1, Number(us[2]))
          : iso
            ? new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))
            : new Date(text);
        if (Number.isNaN(date.getTime())) return text;
      }
      if (Number.isNaN(date.getTime())) return '—';
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${month}/${day}/${date.getFullYear()}`;
    },
    formatWhen(value) {
      if (!value) return '—';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return this.formatDate(value);
      const day = this.formatDate(date);
      const time = new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }).format(date);
      return `${day}, ${time}`;
    },
    displayValue(value) {
      const text = value == null ? '' : String(value).trim();
      return text || '—';
    },
    async loadHistory() {
      if (!this.companyId) return;
      this.loading = true;
      this.statusMessage = '';
      this.statusType = '';
      try {
        const url = `${process.env.VUE_APP_API_URL}/outlook/bill-mail/history?companyId=${encodeURIComponent(this.companyId)}`;
        const response = await fetch(url);
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.error || 'Failed to load history');
        this.records = Array.isArray(result.data) ? result.data : [];
        await Promise.all(this.records.map((row) => this.postedBillAttachments(row).catch(() => [])));
      } catch (error) {
        logger.error('Could not load captured bill history:', error);
        this.records = [];
        this.statusMessage = error.message || 'Could not load history';
        this.statusType = 'error';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.draft-bills-history__scroll {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 1.1rem 3.5rem;
  background: #f6f7f9;
}

.audit-voucher {
  border: 1px solid #e4e7ec;
  border-radius: 0;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}

.audit-voucher__head {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  padding: 0.9rem 1.1rem 1rem;
  border-bottom: 1px solid #eef1f4;
  background: #fff;
}

.audit-voucher__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.audit-voucher__kicker {
  color: #667085;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.audit-voucher__title h3 {
  margin: 0;
  color: #101828;
  font-size: 1.05rem;
  font-weight: 700;
}

.audit-voucher__status {
  margin-left: auto;
  background: #ecfdf3;
  color: #067647;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 0.28rem 0.55rem;
}

.audit-voucher__facts {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) 8.5rem 8.5rem 11rem;
  gap: 0.75rem 1rem;
  margin: 0;
  padding-top: 0.75rem;
  border-top: 1px solid #f2f4f7;
}

.audit-voucher__facts div {
  min-width: 0;
}

.audit-voucher__facts dt {
  margin: 0 0 0.2rem;
  color: #667085;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.audit-voucher__facts dd {
  margin: 0;
  color: #101828;
  font-size: 0.84rem;
  font-weight: 650;
  font-variant-numeric: tabular-nums;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.audit-voucher__flow {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.5rem;
  margin: 0;
  padding: 0.75rem 1.1rem;
  list-style: none;
  background: #fbfcfd;
  border-bottom: 1px solid #eef1f4;
}

.audit-voucher__flow li {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  color: #475467;
  font-size: 0.75rem;
  font-weight: 600;
}

.audit-voucher__flow span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 999px;
  background: #eef2f6;
  color: #344054;
  font-size: 0.68rem;
}

.audit-voucher__flow li:nth-child(1) span {
  background: #fffaeb;
  color: #b54708;
}

.audit-voucher__flow li:nth-child(2) span {
  background: #eef4ff;
  color: #3538cd;
}

.audit-voucher__flow li:nth-child(3) span {
  background: #f4f3ff;
  color: #5925dc;
}

.audit-voucher__flow .is-done span {
  background: #ecfdf3;
  color: #067647;
}

.audit-voucher__docs {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  padding: 0.9rem 1.1rem 0.2rem;
}

.audit-doc {
  border: 1px solid #eef1f4;
  border-radius: 0;
  background: #fcfcfd;
  padding: 0.75rem 0.8rem 0.55rem;
}

.audit-doc--draft {
  border-color: #f3d7b0;
  background: #fff7ed;
}

.audit-doc--draft header i,
.audit-doc--draft header em {
  color: #b54708;
}

.audit-doc--mail {
  border-color: #c7d7fe;
  background: #eef4ff;
}

.audit-doc--mail header i,
.audit-doc--mail header em {
  color: #3538cd;
}

.audit-doc--posted {
  border-color: #abefc6;
  background: #ecfdf3;
}

.audit-doc--posted header i,
.audit-doc--posted header em {
  color: #067647;
}

.audit-doc header {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  margin-bottom: 0.65rem;
}

.audit-doc header i {
  color: #475467;
  font-size: 0.95rem;
  margin-top: 0.1rem;
}

.audit-doc header h4 {
  margin: 0;
  color: #101828;
  font-size: 0.8rem;
  font-weight: 700;
}

.audit-doc header p {
  margin: 0.1rem 0 0;
  color: #667085;
  font-size: 0.7rem;
}

.audit-doc header em {
  margin-left: auto;
  font-style: normal;
  color: #344054;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.audit-doc dl {
  margin: 0;
}

.audit-doc dl div {
  display: grid;
  grid-template-columns: 6.4rem minmax(0, 1fr);
  gap: 0.45rem;
  padding: 0.38rem 0;
  border-top: 1px solid rgba(16, 24, 40, 0.06);
  font-size: 0.78rem;
}

.audit-doc dt {
  color: #667085;
}

.audit-doc dd {
  margin: 0;
  color: #101828;
  overflow-wrap: anywhere;
}

.audit-doc .is-amount dd {
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  text-align: right;
}

.audit-files {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-top: 0.55rem;
  padding-top: 0.55rem;
  border-top: 1px solid rgba(16, 24, 40, 0.06);
}

.audit-files > span {
  color: #667085;
  font-size: 0.72rem;
}

.audit-files__btn {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  padding: 0.4rem 0.5rem;
  border: 1px solid #c7d7fe;
  border-radius: 0;
  background: #fff;
  color: #3538cd;
  font-size: 0.75rem;
  text-align: left;
  cursor: pointer;
}

.audit-files__btn span {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.audit-files__btn:hover:not(:disabled) {
  background: #eef4ff;
}

.audit-files__btn:disabled {
  opacity: 0.7;
  cursor: wait;
}

.audit-viewer {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
}

.audit-viewer__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(16, 24, 40, 0.55);
}

.audit-viewer__sheet {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  width: min(920px, 100%);
  height: min(92vh, 860px);
  border-radius: 0;
  background: #fff;
  box-shadow: 0 24px 48px rgba(16, 24, 40, 0.22);
  overflow: hidden;
}

.audit-viewer__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #eef1f4;
  background: #f8faff;
}

.audit-viewer__bar span {
  display: block;
  color: #667085;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.audit-viewer__bar strong {
  display: block;
  margin-top: 0.15rem;
  color: #101828;
  font-size: 0.9rem;
}

.audit-viewer__actions {
  display: flex;
  align-items: center;
  gap: 0.45rem;
}

.audit-viewer__actions button,
.audit-viewer__message button {
  border: 1px solid #d0d5dd;
  border-radius: 0;
  background: #fff;
  color: #344054;
  font-size: 0.78rem;
  font-weight: 650;
  padding: 0.4rem 0.7rem;
  cursor: pointer;
}

.audit-viewer__close {
  border-color: #c7d7fe !important;
  background: #eef4ff !important;
  color: #3538cd !important;
}

.audit-viewer__page {
  flex: 1;
  width: 100%;
  min-height: 0;
  border: 0;
  background: #f2f4f7;
}

.audit-viewer__page img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.audit-viewer__message {
  margin: 0;
  padding: 1.25rem 1rem;
  color: #475467;
  font-size: 0.85rem;
}

.audit-adjust {
  margin: 0.85rem 1.1rem 1rem;
  border: 1px solid #ddd6fe;
  border-radius: 0;
  overflow: hidden;
  background: linear-gradient(180deg, #faf8ff 0%, #fff 28%);
}

.audit-adjust header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 0.85rem;
  background: #fbfcfd;
  border-bottom: 1px solid #eef1f4;
}

.audit-adjust h4 {
  margin: 0;
  color: #101828;
  font-size: 0.8rem;
  font-weight: 700;
}

.audit-adjust header p {
  margin: 0.15rem 0 0;
  color: #667085;
  font-size: 0.72rem;
}

.audit-adjust header span,
.audit-adjust__none {
  color: #475467;
  font-size: 0.75rem;
}

.audit-adjust header span {
  font-weight: 700;
}

.audit-adjust__none {
  margin: 0;
  padding: 0.8rem 0.85rem;
}

.audit-adjust table {
  width: 100%;
  border-collapse: collapse;
}

.audit-adjust th,
.audit-adjust td {
  padding: 0.62rem 0.85rem;
  border-top: 1px solid #f2f4f7;
  text-align: left;
  vertical-align: top;
  font-size: 0.78rem;
  line-height: 1.4;
}

.audit-adjust thead th {
  color: #667085;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: #fff;
}

.audit-adjust tbody th {
  width: 22%;
  color: #344054;
  font-weight: 650;
}

.audit-adjust td {
  color: #101828;
  overflow-wrap: anywhere;
  white-space: normal;
}

.audit-adjust td:last-child {
  font-weight: 650;
}

@media (max-width: 1180px) {
  .audit-voucher__docs {
    display: block;
  }

  .audit-voucher__facts {
    grid-template-columns: 1fr 1fr;
  }

  .audit-voucher__docs {
    display: grid;
    grid-template-columns: 1fr;
  }

  .audit-voucher__flow {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
